/**
 * Details tab — metadata for the image or video currently shown on the main workspace frame.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { Box, CircularProgress, IconButton, Skeleton, Tooltip, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { useBreakdownHistoryIds, useBreakdownHistorySnapshots, useImageEntity, useMediaEntity, useProjectVideoIds, type MediaEntityKind } from "@/api/api";
import { useThumbnailImageSrc } from "@/hooks/useThumbnailImageSrc";
import { useProjectPermissions } from "@/hooks/useProjectPermissions";
import type { MediaEntityInfo } from "@/types/api";
import type { ProjectDetailsStoryboardShot } from "@/types/project";
import { ProjectPanelRoute } from "@/types/routes";
import { extractPromptDescription } from "@/services/generation-prompts";
import { storyboardWorkingStillImageId } from "@/services/storyboard";
import { formatStyleDateTime } from "@/utils/styleDna";
import { partitionVideoEntityParents } from "@/utils/videoEntityParents";
import { isOmniEditVideoModel, isOmniSource, readProviderId } from "@/utils/videoExtend";
import {
  findBreakdownSnapshotSourceForMediaId,
  formatBreakdownHistoryDate,
  type BreakdownSnapshotMediaSource
} from "@/components/ProjectPage/RightSidebar/libraryItems";

interface MainFrameMediaTarget {
  readonly kind: MediaEntityKind;
  readonly id: string;
}

function resolveMainFrameMediaTarget(args: {
  readonly panel: string | undefined;
  readonly shot: ProjectDetailsStoryboardShot | undefined;
  readonly characterPrimaryImageId: string | null;
  readonly locationPrimaryImageId: string | null;
  readonly completedStoryboardVideoId: string | null;
}): MainFrameMediaTarget | null {
  const { panel, shot, characterPrimaryImageId, locationPrimaryImageId, completedStoryboardVideoId } = args;

  if (panel === ProjectPanelRoute.Characters) {
    return characterPrimaryImageId ? { kind: "image", id: characterPrimaryImageId } : null;
  }

  if (panel === ProjectPanelRoute.Locations) {
    return locationPrimaryImageId ? { kind: "image", id: locationPrimaryImageId } : null;
  }

  if (panel === ProjectPanelRoute.ImageFrame) {
    const imageId = storyboardWorkingStillImageId(shot);
    return imageId ? { kind: "image", id: imageId } : null;
  }

  if (completedStoryboardVideoId) {
    return { kind: "video", id: completedStoryboardVideoId };
  }

  const stillImageId = storyboardWorkingStillImageId(shot);
  return stillImageId ? { kind: "image", id: stillImageId } : null;
}

function displayOrDash(value: string | null | undefined): string {
  const trimmed = value?.trim();
  return trimmed !== undefined && trimmed !== null && trimmed !== "" ? trimmed : "—";
}

function numberOrDash(value: number | null | undefined): string {
  return typeof value === "number" && Number.isFinite(value) ? String(value) : "—";
}

interface DetailFieldProps {
  readonly label: string;
  readonly value: string;
  readonly multiline?: boolean;
  readonly copyable?: boolean;
  readonly monospace?: boolean;
  readonly copyValue?: string;
  readonly copyLabel?: string;
}

const DetailField = ({
  label,
  value,
  multiline = false,
  copyable = false,
  monospace = false,
  copyValue,
  copyLabel
}: DetailFieldProps) => {
  const [copied, setCopied] = useState(false);
  const copiedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const effectiveCopyValue = (copyValue ?? value).trim();
  const effectiveCopyLabel = copyLabel ?? `Copy ${label.toLowerCase()}`;

  useEffect(() => {
    return () => {
      if (copiedTimeoutRef.current !== null) {
        clearTimeout(copiedTimeoutRef.current);
      }
    };
  }, []);

  const canCopy = copyable && value !== "—" && effectiveCopyValue.length > 0;

  const handleCopy = useCallback(async () => {
    if (!canCopy) {
      return;
    }
    try {
      await navigator.clipboard.writeText(effectiveCopyValue);
      setCopied(true);
      if (copiedTimeoutRef.current !== null) {
        clearTimeout(copiedTimeoutRef.current);
      }
      copiedTimeoutRef.current = setTimeout(() => {
        setCopied(false);
        copiedTimeoutRef.current = null;
      }, 3000);
    } catch {
      // Clipboard access may be unavailable; fail silently.
    }
  }, [canCopy, effectiveCopyValue]);

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.25 }}>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {label}
        </Typography>
        {copyable && canCopy ? (
          copied ? (
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              copied..
            </Typography>
          ) : (
            <Tooltip title={effectiveCopyLabel} arrow>
              <IconButton
                size="small"
                onClick={() => {
                  void handleCopy();
                }}
                aria-label={effectiveCopyLabel}
                sx={{ p: 0.25, color: "text.secondary" }}
              >
                <ContentCopyIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )
        ) : null}
      </Box>
      <Typography
        variant="body2"
        sx={{
          color: "text.primary",
          ...(monospace ? { fontFamily: "monospace" } : {}),
          ...(multiline
            ? { whiteSpace: "pre-wrap", wordBreak: "break-word" }
            : { overflow: "hidden", textOverflow: "ellipsis" })
        }}
      >
        {value}
      </Typography>
    </Box>
  );
};

interface ParentImageThumbnailProps {
  readonly projectId: string;
  readonly imageId: string;
}

const ParentImageThumbnail = ({ projectId, imageId }: ParentImageThumbnailProps) => {
  const { data, isLoading, isError } = useImageEntity(projectId, imageId);
  const { src: thumbnailSrc, onError: onThumbnailError } = useThumbnailImageSrc(data);
  const title = data?.displayName ?? imageId;

  return (
    <Box
      sx={{
        width: 88,
        flexShrink: 0
      }}
    >
      <Box
        sx={{
          aspectRatio: "16/9",
          borderRadius: 1,
          overflow: "hidden",
          bgcolor: "action.hover",
          border: 1,
          borderColor: "divider"
        }}
      >
        {isLoading ? (
          <Skeleton variant="rectangular" width="100%" height="100%" animation="wave" />
        ) : isError || !data?.url ? (
          <Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Typography variant="caption" sx={{ color: "text.secondary", px: 0.5, textAlign: "center" }}>
              —
            </Typography>
          </Box>
        ) : (
          <Box
            component="img"
            src={thumbnailSrc}
            crossOrigin="anonymous"
            onError={onThumbnailError}
            loading="lazy"
            decoding="async"
            alt={title}
            sx={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
          />
        )}
      </Box>
      <Typography
        variant="caption"
        sx={{
          display: "block",
          mt: 0.5,
          color: "text.secondary",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap"
        }}
      >
        {title}
      </Typography>
    </Box>
  );
};

interface ParentVideoThumbnailProps {
  readonly projectId: string;
  readonly videoId: string;
  readonly onPlayVideo?: (videoId: string) => void;
}

interface VideoParentVideoSectionProps {
  readonly projectId: string | null;
  readonly info: MediaEntityInfo | undefined;
  readonly projectVideoIds: readonly string[];
  readonly fallbackParentVideoId: string | null;
  readonly onPlayParentVideo: (videoId: string) => void;
}

/** Parent video lineage — isolated so metadata updates always re-render this block. */
const VideoParentVideoSection = ({
  projectId,
  info,
  projectVideoIds,
  fallbackParentVideoId,
  onPlayParentVideo
}: VideoParentVideoSectionProps) => {
  const { parentVideoId } = partitionVideoEntityParents(info, projectVideoIds);
  const resolvedParentVideoId =
    parentVideoId ?? (isOmniEditVideoModel(info?.source) ? fallbackParentVideoId : null);
  const showSection = resolvedParentVideoId !== null || isOmniEditVideoModel(info?.source);

  if (!showSection) {
    return null;
  }

  let thumbnail: ReactNode = (
    <Typography variant="body2" sx={{ color: "text.secondary" }}>
      —
    </Typography>
  );

  if (resolvedParentVideoId !== null && projectId) {
    thumbnail = (
      <ParentVideoThumbnail projectId={projectId} videoId={resolvedParentVideoId} onPlayVideo={onPlayParentVideo} />
    );
  }

  return (
    <Box>
      <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 0.75 }}>
        Parent video
      </Typography>
      {thumbnail}
    </Box>
  );
};

const ParentVideoThumbnail = ({ projectId, videoId, onPlayVideo }: ParentVideoThumbnailProps) => {
  const theme = useTheme();
  const { data, isLoading, isError } = useMediaEntity(projectId, videoId, "video");
  const title = data?.displayName ?? videoId;
  const canPlay = Boolean(onPlayVideo && data?.url);

  return (
    <Box
      sx={{
        width: 88,
        flexShrink: 0
      }}
    >
      <Box
        sx={{
          aspectRatio: "16/9",
          borderRadius: 1,
          overflow: "hidden",
          bgcolor: "action.hover",
          border: 1,
          borderColor: "divider",
          position: "relative"
        }}
      >
        {isLoading ? (
          <Skeleton variant="rectangular" width="100%" height="100%" animation="wave" />
        ) : isError || !data?.url ? (
          <Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Typography variant="caption" sx={{ color: "text.secondary", px: 0.5, textAlign: "center" }}>
              —
            </Typography>
          </Box>
        ) : (
          <>
            <Box
              component="video"
              src={data.url}
              muted
              playsInline
              preload="metadata"
              sx={{ width: "100%", height: "100%", objectFit: "contain", display: "block", bgcolor: "common.black" }}
            />
            {canPlay ? (
              <IconButton
                size="small"
                onClick={() => onPlayVideo?.(videoId)}
                aria-label="Play video"
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  zIndex: 1,
                  bgcolor: alpha(theme.palette.common.black, 0.4),
                  color: theme.palette.common.white,
                  "&:hover": {
                    bgcolor: alpha(theme.palette.common.black, 0.6)
                  }
                }}
              >
                <PlayArrowIcon sx={{ fontSize: 16 }} />
              </IconButton>
            ) : null}
          </>
        )}
      </Box>
      <Typography
        variant="caption"
        sx={{
          display: "block",
          mt: 0.5,
          color: "text.secondary",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap"
        }}
      >
        {title}
      </Typography>
    </Box>
  );
};

/** Read-only text a past breakdown snapshot preserved about the asset on the main frame. */
const PreviousBreakdownSection = ({ source }: { readonly source: BreakdownSnapshotMediaSource }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1, borderTop: 1, borderColor: "divider" }}>
    <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>
      Previous breakdown — {formatBreakdownHistoryDate(source.snapshotDate)}
    </Typography>
    {source.kind === "shot" ? (
      <>
        <DetailField label="Shot description" value={displayOrDash(source.shotDescription)} multiline />
        <DetailField label="Ref image prompt" value={displayOrDash(source.refImagePrompt)} multiline copyable />
        <DetailField label="Video gen prompt" value={displayOrDash(source.videoGenPrompt)} multiline copyable />
        <DetailField label="Notes" value={displayOrDash(source.notes)} multiline />
        <DetailField label="Dialogues" value={displayOrDash(source.dialogues)} multiline />
      </>
    ) : (
      <>
        <DetailField label="Name" value={displayOrDash(source.name)} />
        <DetailField label="Description" value={displayOrDash(source.description)} multiline />
        <DetailField label="Appearance" value={displayOrDash(source.appearance)} multiline />
      </>
    )}
  </Box>
);

export interface DetailsTabPanelProps {
  readonly projectId: string | null;
  readonly panel: string | undefined;
  readonly shot: ProjectDetailsStoryboardShot | undefined;
  readonly characterPrimaryImageId: string | null;
  readonly locationPrimaryImageId: string | null;
  readonly completedStoryboardVideoId: string | null;
  readonly onPlayParentVideo: (videoId: string) => void;
}

const DetailsTabPanel = ({
  projectId,
  panel,
  shot,
  characterPrimaryImageId,
  locationPrimaryImageId,
  completedStoryboardVideoId,
  onPlayParentVideo
}: DetailsTabPanelProps) => {
  const { isOwner, isLocked, ownerEmail } = useProjectPermissions();
  const target = resolveMainFrameMediaTarget({
    panel,
    shot,
    characterPrimaryImageId,
    locationPrimaryImageId,
    completedStoryboardVideoId
  });

  const entityQuery = useMediaEntity(
    projectId,
    target?.id ?? null,
    target?.kind ?? "image"
  );
  const { data: projectVideoIds = [] } = useProjectVideoIds(projectId, {
    enabled: target?.kind === "video"
  });

  const { data: breakdownHistoryIds = [] } = useBreakdownHistoryIds(projectId, { enabled: Boolean(projectId) });
  const { snapshots: breakdownHistorySnapshots } = useBreakdownHistorySnapshots(projectId, breakdownHistoryIds, Boolean(projectId));
  const breakdownSource = useMemo(
    () => findBreakdownSnapshotSourceForMediaId(breakdownHistorySnapshots, target?.id ?? null),
    [breakdownHistorySnapshots, target]
  );

  if (!target) {
    return (
      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", px: 2, py: 3 }}>
        <Typography variant="body2" sx={{ color: "text.secondary", textAlign: "center" }}>
          No asset on the main frame
        </Typography>
      </Box>
    );
  }

  if (entityQuery.isPending) {
    return (
      <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (entityQuery.isError) {
    return (
      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", px: 2, py: 3 }}>
        <Typography variant="body2" color="error" sx={{ textAlign: "center" }}>
          Failed to load asset details
        </Typography>
      </Box>
    );
  }

  const info = entityQuery.data?.info;
  const { parentImageIds } = partitionVideoEntityParents(info, projectVideoIds);
  const fallbackParentVideoId = shot?.["video-uploaded-images"]?.[0]?.trim() || null;

  let parentThumbnails: ReactNode = (
    <Typography variant="body2" sx={{ color: "text.secondary" }}>
      —
    </Typography>
  );

  if (parentImageIds.length > 0 && projectId) {
    parentThumbnails = (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
        {parentImageIds.map((imageId) => (
          <ParentImageThumbnail key={imageId} projectId={projectId} imageId={imageId} />
        ))}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        overflow: "auto",
        px: 2,
        py: 2,
        display: "flex",
        flexDirection: "column",
        gap: 2
      }}
    >
      <DetailField label="Display name" value={displayOrDash(info?.display_name ?? entityQuery.data?.displayName)} />
      <DetailField label="Created date" value={formatStyleDateTime(info?.created_date)} />
      <DetailField label="Created by" value={displayOrDash(info?.created_by)} />
      <DetailField
        label="Project status"
        value={isLocked ? `Locked by ${isOwner ? "you" : (ownerEmail ?? "the owner")}` : "Unlocked - anyone can edit"}
      />
      <DetailField
        label="Prompt"
        value={displayOrDash(info?.prompt ?? undefined)}
        copyValue={extractPromptDescription(info?.prompt)}
        copyLabel="Copy user prompt"
        multiline
        copyable
      />
      <DetailField label="Source" value={displayOrDash(info?.source)} />
      {info?.seed === undefined ? null : <DetailField label="Seed" value={numberOrDash(info.seed)} copyable monospace />}
      {info?.temperature === undefined ? null : (
        <DetailField label="Temperature" value={numberOrDash(info.temperature)} monospace />
      )}
      {target.kind === "video" && isOmniSource(info?.source) ? (
        <DetailField label="Session ID" value={displayOrDash(readProviderId(info) || undefined)} multiline copyable monospace />
      ) : null}
      <Box>
        <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 0.75 }}>
          Parent images
        </Typography>
        {parentThumbnails}
      </Box>
      {target.kind === "video" ? (
        <VideoParentVideoSection
          projectId={projectId}
          info={info}
          projectVideoIds={projectVideoIds}
          fallbackParentVideoId={fallbackParentVideoId}
          onPlayParentVideo={onPlayParentVideo}
        />
      ) : null}
      {breakdownSource ? <PreviousBreakdownSection source={breakdownSource} /> : null}
    </Box>
  );
};

export default DetailsTabPanel;
